/**
 * Runtime map of Liquidity icon export names → React components for marketing message artifacts.
 * Keep registry keys in sync with CMT `marketingMessageIconAllowlist.ts`
 * (`MARKETING_MESSAGE_ICON_ALLOWLIST`). Allowlist strings must match
 * `packages/liquidity/src/icons/index.ts` export names exactly. Unknown or missing keys fall back to
 * LightBulbGearIcon.
 *
 * Nav items do not come through here. Their icons are imported directly in
 * `SideNavigation/navigationConfigRegistry.ts`, because a NAVIGATION artifact no longer carries an
 * icon Enterprise reads (SUPP-19611).
 *
 * Client-widget-only icons (`CalendarClockIcon`) are imported directly in `useClientMarketingWidget` — not registered here.
 */
import type { ComponentType, SVGProps } from 'react';
import {
  ChartDoubleLineIcon,
  ChartLineIcon,
  CircleCheckIcon,
  ClipboardClockIcon,
  Coins2Icon,
  FastBillIcon,
  GrowFundsIcon,
  HandBill2Icon,
  HandshakeIcon,
  InvoiceCalculateIcon,
  InvoiceIcon,
  InvoicesIcon,
  LightBulbGearIcon,
  PaperBillsIcon,
  PercentageClockIcon,
  PercentageDownIcon,
  PercentageIcon,
  PointTapIcon,
  RabbitFastIcon,
  RotateBillIcon,
  StackBillsIcon,
  TicketIcon,
  TypingIcon,
  UploadIcon,
  WalletIcon,
} from '@c2fo/liquidity/icons';

export const MARKETING_MESSAGE_ICON_ALLOWLIST = [
  'ChartDoubleLineIcon',
  'ChartLineIcon',
  'CircleCheckIcon',
  'ClipboardClockIcon',
  'Coins2Icon',
  'FastBillIcon',
  'HandBill2Icon',
  'HandshakeIcon',
  'GrowFundsIcon',
  'InvoiceCalculateIcon',
  'InvoiceIcon',
  'InvoicesIcon',
  'LightBulbGearIcon',
  'PaperBillsIcon',
  'PercentageIcon',
  'PercentageClockIcon',
  'PercentageDownIcon',
  'PointTapIcon',
  'RabbitFastIcon',
  'RotateBillIcon',
  'StackBillsIcon',
  'TicketIcon',
  'TypingIcon',
  'UploadIcon',
  'WalletIcon',
] as const;

export type MarketingMessageIconKey = (typeof MARKETING_MESSAGE_ICON_ALLOWLIST)[number];

export const DEFAULT_MARKETING_MESSAGE_ICON_KEY: MarketingMessageIconKey = 'LightBulbGearIcon';

type LiquidityIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export const MARKETING_MESSAGE_ICON_REGISTRY: Record<MarketingMessageIconKey, LiquidityIconComponent> = {
  ChartDoubleLineIcon,
  ChartLineIcon,
  CircleCheckIcon,
  ClipboardClockIcon,
  Coins2Icon,
  FastBillIcon,
  HandBill2Icon,
  HandshakeIcon,
  GrowFundsIcon,
  InvoiceCalculateIcon,
  InvoiceIcon,
  InvoicesIcon,
  LightBulbGearIcon,
  PaperBillsIcon,
  PercentageClockIcon,
  PercentageIcon,
  PercentageDownIcon,
  PointTapIcon,
  RabbitFastIcon,
  RotateBillIcon,
  StackBillsIcon,
  TicketIcon,
  TypingIcon,
  UploadIcon,
  WalletIcon,
};

export function resolveMarketingMessageIconKey(iconKey: string | undefined): MarketingMessageIconKey {
  if (iconKey && Object.hasOwn(MARKETING_MESSAGE_ICON_REGISTRY, iconKey)) {
    return iconKey as MarketingMessageIconKey;
  }

  return DEFAULT_MARKETING_MESSAGE_ICON_KEY;
}

export function resolveMarketingMessageIcon(iconKey: string | undefined): LiquidityIconComponent {
  return MARKETING_MESSAGE_ICON_REGISTRY[resolveMarketingMessageIconKey(iconKey)];
}
